import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { AlertStatusBadge } from '@/components/AlertStatusBadge'
import { HorizontalScrollArea } from '@/components/HorizontalScrollArea'
import { Badge } from '@/components/ui/badge'
import { formatLastSeen, formatRam } from '@/lib/formatAgent'
import { alertStatusSortValue } from '@/lib/alertStatus'
import {
  formatToolkitScore,
  toolkitStatusLabel,
  toolkitStatusVariant,
} from '@/lib/toolkitHealth'
import { sortRows } from '@/lib/sortTable'
import { cn } from '@/lib/utils'

function formatCell(colId, value) {
  if (value == null || value === '') return '—'
  if (colId === 'ram_mb') return formatRam(value)
  if (
    colId === 'last_seen' ||
    colId === 'created' ||
    colId === 'cleared' ||
    colId === 'archived_at' ||
    colId === 'synced_at' ||
    colId === 'install_date'
  ) {
    return formatLastSeen(value)
  }
  if (
    colId === 'online_status' ||
    colId === 'threatdown_online' ||
    colId === 'bitlocker_encrypted' ||
    colId === 'reboot_required' ||
    colId === 'macros_allowed' ||
    colId === 'secure_boot' ||
    colId === 'tpm_enabled'
  ) {
    return value === true || value === 'true' || value === 'Yes'
      ? 'Yes'
      : value === false || value === 'false' || value === 'No'
        ? 'No'
        : String(value)
  }
  if (colId === 'lowest_disk_free_pct' && value != null && value !== '') {
    return `${value}%`
  }
  if (colId === 'toolkit_score' && value != null && value !== '') {
    return formatToolkitScore(value)
  }
  if (colId === 'toolkit_health' && value) {
    return toolkitStatusLabel(value)
  }
  if (colId === 'machine_type' && typeof value === 'string') {
    return value.charAt(0).toUpperCase() + value.slice(1)
  }
  return String(value)
}

function reportSortValue(colId, row) {
  const value = row[colId]
  if (colId === 'is_active') return alertStatusSortValue(value)
  if (
    colId === 'created' ||
    colId === 'cleared' ||
    colId === 'archived_at' ||
    colId === 'last_seen' ||
    colId === 'synced_at' ||
    colId === 'install_date'
  ) {
    return value ? new Date(value).getTime() : null
  }
  if (
    colId === 'ram_mb' ||
    colId === 'count' ||
    colId === 'install_count' ||
    colId === 'agent_id' ||
    colId === 'lowest_disk_free_pct' ||
    colId === 'local_admin_count' ||
    colId === 'failed_logins_today' ||
    colId === 'admin_changes_today' ||
    colId === 'toolkit_score' ||
    colId === 'toolkit_failures' ||
    colId === 'toolkit_warnings' ||
    colId === 'toolkit_checks_run'
  ) {
    const n = Number(value)
    return Number.isNaN(n) ? null : n
  }
  if (typeof value === 'boolean') return value ? 1 : 0
  return value == null ? '' : String(value).toLowerCase()
}

function ReportCell({ col, row }) {
  if (col.id === 'is_active') {
    return <AlertStatusBadge isActive={row.is_active} />
  }
  if (col.id === 'toolkit_health' && row.toolkit_health) {
    return (
      <Badge variant={toolkitStatusVariant(row.toolkit_health)}>
        {toolkitStatusLabel(row.toolkit_health)}
      </Badge>
    )
  }
  if (col.id === 'hostname' && row.agent_id) {
    return (
      <Link
        to={`/agents/${row.agent_id}`}
        className="text-primary hover:underline"
      >
        {row.hostname || `Device ${row.agent_id}`}
      </Link>
    )
  }
  if (col.id === 'title' && row.title) {
    return <span title={row.title}>{row.title}</span>
  }
  if (col.id === 'message' && row.message) {
    return (
      <span className="block max-w-md truncate" title={row.message}>
        {row.message}
      </span>
    )
  }
  if (col.id === 'device_name' && row.agent_id) {
    return (
      <Link
        to={`/agents/${row.agent_id}`}
        className="text-primary hover:underline"
        title={row.device_name}
      >
        {row.device_name || row.hostname || `Device ${row.agent_id}`}
      </Link>
    )
  }
  return formatCell(col.id, row[col.id])
}

function SortableReportHeader({ col, sortBy, sortDir, onSort }) {
  const active = sortBy === col.id
  return (
    <th
      className="px-3 py-2 text-left font-medium whitespace-nowrap cursor-pointer select-none hover:text-foreground"
      onClick={() => onSort(col.id)}
    >
      <span className="inline-flex items-center gap-1">
        {col.label}
        {active ? (
          sortDir === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 shrink-0 opacity-40" />
        )}
      </span>
    </th>
  )
}

export function ReportTable({ rows, visibleColumns, defaultSort, maxRows = null }) {
  const firstCol = visibleColumns[0]?.id
  const [sortBy, setSortBy] = useState(defaultSort?.id ?? firstCol)
  const [sortDir, setSortDir] = useState(defaultSort?.dir ?? 'asc')

  const sortColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        id: col.id,
        sortValue: (row) => reportSortValue(col.id, row),
      })),
    [visibleColumns],
  )

  const sorted = useMemo(
    () => sortRows(rows, sortBy, sortDir, sortColumns),
    [rows, sortBy, sortDir, sortColumns],
  )

  const displayRows =
    maxRows != null && maxRows > 0 ? sorted.slice(0, maxRows) : sorted

  function handleSort(columnId) {
    if (sortBy === columnId) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(columnId)
      setSortDir('asc')
    }
  }

  function rowKey(row, index) {
    // Always include index — multi-row reports (software, patches) reuse agent_id.
    return [
      row.atera_id,
      row.agent_id,
      row.software,
      row.title,
      row.kb_number,
      row.hostname,
      index,
    ]
      .filter((part) => part != null && part !== '')
      .join('|')
  }

  if (!rows.length) {
    return (
      <p className="text-sm text-muted-foreground">No data for this report.</p>
    )
  }

  if (!visibleColumns.length) {
    return (
      <p className="text-sm text-muted-foreground">
        Select at least one column to display.
      </p>
    )
  }

  return (
    <div>
      <HorizontalScrollArea mainClassName="max-h-[min(70vh,42rem)] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted/80">
            <tr>
              {visibleColumns.map((col) => (
                <SortableReportHeader
                  key={col.id}
                  col={col}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, i) => (
              <tr key={rowKey(row, i)} className="border-t border-border/60">
                {visibleColumns.map((col) => (
                  <td
                    key={col.id}
                    className={cn(
                      'px-3 py-2 whitespace-nowrap max-w-[320px] truncate',
                      col.id === 'message' && 'max-w-md',
                    )}
                  >
                    <ReportCell col={col} row={row} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </HorizontalScrollArea>
      <p className="mt-2 text-xs text-muted-foreground">
        {maxRows != null && rows.length > maxRows
          ? `Showing first ${maxRows} of ${rows.length} rows. Export CSV for the full set.`
          : `Showing all ${rows.length.toLocaleString()} rows.`}
      </p>
    </div>
  )
}
